/* @flow */
/**
 * Created by Astha on 4/11/2018.
 */
import _ from 'lodash';
import { ZmCollection } from '../../../autogenerated/ZoomBridge';
import CollectionUtil from '../CollectionUtil';
import CollectionFeature from '../CollectionFeature';
import Store from '../../../store/Store';
import { COLLECTION_METADATA } from '../../../store/Constants';
import HashMap from '../../../protocol/HashMap';

export default class CollectionSettingsUtil {
  //n order to call a static method within another static method of the same class, you can use the this keyword.

  static canSetExpire(collection: ZmCollection): boolean {
    return this.isAdmin(collection) || this.isOwner(collection) || this.isSuperAdmin(collection);
  }

  static canSetDownloadParams(collection: ZmCollection): boolean {
    return this.isSuperAdmin(collection) || this.isOwner(collection);
  }

  static canSetDefaultProject(collection: ZmCollection): boolean {
    return this.isAdmin(collection) || this.isOwner(collection) || this.isSuperAdmin(collection);
  }

  static canSetExternalCollection(collection: ZmCollection): boolean {
    const { IS_COLLECTION_TYPE_TOGGLE_ALLOWED } = CollectionUtil.COLLECTION_PARAM_KEYS;
    const metadata: HashMap = Store.get(COLLECTION_METADATA) || new HashMap();
    const canToggle: string[] = metadata.get(IS_COLLECTION_TYPE_TOGGLE_ALLOWED) || [];
    if (canToggle[0] === 'true') {
      return CollectionSettingsUtil.isOwner(collection) || CollectionSettingsUtil.isSuperAdmin(collection);
    }
    return false;
  }

  static canArchiveCollection(collection: ZmCollection): boolean {
    return this.canDeleteCollection(collection);
  }

  static canDeleteCollection(collection: ZmCollection): boolean {
    return (
      this.isAdmin(collection) ||
      this.isOwner(collection) ||
      this.isSuperAdmin(collection) ||
      this.hasTrue(collection, CollectionFeature.CAN_DELETE_COLLECTION)
    );
  }

  static canResolveComments(collection: ZmCollection): boolean {
    return this.isAdmin(collection) || this.isSuperAdmin(collection) || this.isOwner(collection);
  }

  static canViewComments(collection: ZmCollection) {
    return (
      this.isAdmin(collection) ||
      this.isOwner(collection) ||
      this.isSuperAdmin(collection) ||
      this.hasTrue(collection, CollectionFeature.VIEW_COMMENTS)
    );
  }

  static canWriteComments(collection: ZmCollection) {
    return (
      this.isAdmin(collection) ||
      this.isOwner(collection) ||
      this.isSuperAdmin(collection) ||
      this.hasTrue(collection, CollectionFeature.WRITE_COMMENTS)
    );
  }

  static canRenameCollection(collection: ZmCollection) {
    return (
      this.isAdmin(collection) || this.isOwner(collection) || this.isSuperAdmin(collection) //||
      //this.hasTrue(collection, CollectionFeature.CAN_DELETE_COLLECTION)
    );
  }

  static canRemovePeople(collection: ZmCollection) {
    return (
      this.isAdmin(collection) ||
      this.isOwner(collection) ||
      this.isSuperAdmin(collection) ||
      this.hasTrue(collection, CollectionFeature.CAN_SHARE_COLLECTION)
    );
  }

  static canAddAssets(collection: ZmCollection) {
    return (
      this.isAdmin(collection) ||
      this.isOwner(collection) ||
      this.isSuperAdmin(collection) ||
      this.hasTrue(collection, CollectionFeature.ADD_ASSETS)
    );
  }

  static canRemoveAssets(collection: ZmCollection) {
    return this.canAddAssets(collection);
  }

  static canShare(collection: ZmCollection) {
    return (
      this.isAdmin(collection) ||
      this.isOwner(collection) ||
      this.isSuperAdmin(collection) ||
      this.hasTrue(collection, CollectionFeature.CAN_SHARE_COLLECTION)
    );
  }

  static canDeleteTag(collection: ZmCollection) {
    return this.isAdmin(collection);
  }

  static canAddTag(collection: ZmCollection) {
    return this.canWriteComments(collection);
  }

  static canFavorite(collection: ZmCollection) {
    return this.isAdmin(collection) || this.isSuperAdmin(collection) || this.isOwner(collection);
  }

  static canShowPopOver(collection: ZmCollection) {
    return this.isAdmin(collection) || this.isSuperAdmin(collection) || this.isOwner(collection);
  }

  static canShowSpotLightAction(collection: ZmCollection) {
    return this.isAdmin(collection) || this.isSuperAdmin(collection) || this.isOwner(collection);
  }

  static canApproveOrReject(collection: ZmCollection) {
    return this.canWriteComments(collection);
  }

  static canChangeParameters(collection: ZmCollection) {
    return this.isAdmin(collection) || this.isSuperAdmin(collection) || this.isOwner(collection);
  }

  static isOwner(collection: ZmCollection) {
    return CollectionFeature.hasTrue(collection.getRequestUserPermissionsMap(collection), CollectionFeature.OWNER);
  }

  static isSuperAdmin(collection: ZmCollection) {
    return CollectionFeature.hasTrue(
      collection.getRequestUserPermissionsMap(collection),
      CollectionFeature.SUPER_ADMIN
    );
  }

  static isAdmin(collection: ZmCollection) {
    return CollectionFeature.hasTrue(collection.getRequestUserPermissionsMap(collection), CollectionFeature.ADMIN);
  }

  static isOwnerOrSuperAdmin(collection: ZmCollection) {
    return this.isOwner(collection) || this.isSuperAdmin(collection);
  }

  static isOwnerOrSuperAdminOrAdmin(collection: ZmCollection) {
    return this.isOwnerOrSuperAdmin(collection) || this.isAdmin(collection);
  }

  static hasTrue(collection: ZmCollection, featureCode: CollectionFeature): boolean {
    return CollectionFeature.hasTrue(collection.getRequestUserPermissionsMap(collection), featureCode);
  }

  static canDownload(collection: ZmCollection): boolean {
    const { DOWNLOAD } = CollectionUtil.COLLECTION_PARAM_KEYS;
    const { NO_DOWNLOADS_PERMITTED } = CollectionUtil.COLLECTION_PARAMETER_VALUES;
    const downloadValues = Store.get(COLLECTION_METADATA).get(DOWNLOAD);

    return (
      CollectionSettingsUtil.isOwner(collection) ||
      CollectionSettingsUtil.isAdmin(collection) ||
      CollectionSettingsUtil.isSuperAdmin(collection) ||
      !_.includes(downloadValues, NO_DOWNLOADS_PERMITTED)
    );
  }
}
